import type { BehaviorNode } from "../blueprint/types.js";
import { createValueCompiler } from "./compileValue.js";
import type {
  CompiledRowVariantRule,
  CompiledValue,
  IndexedGraph,
  SceneCatalogDependencyIndex,
} from "./types.js";

function isCreateRowVariantNode(node: BehaviorNode): boolean {
  return node.type === "action" && node.kind === "createRowVariant";
}

function readPortDefaultValue(node: BehaviorNode, portIds: string[]): unknown {
  for (const portId of portIds) {
    const port = node.ports.find((entry) => entry.id === portId);
    if (port?.value != null) {
      return port.value;
    }
  }

  return undefined;
}

function hasConnectedConditionPort(
  indexed: IndexedGraph,
  nodeId: string,
): boolean {
  return indexed.inputConnectionByNodeAndPort.has(`${nodeId}:condition`);
}

/**
 * Compiles `createRowVariant` actions into per-row template selection rules.
 */
export function compileRowVariantRules(
  indexed: IndexedGraph,
  catalogDeps?: SceneCatalogDependencyIndex,
): CompiledRowVariantRule[] {
  const valueCompiler = createValueCompiler(indexed, catalogDeps);
  const rules: CompiledRowVariantRule[] = [];

  for (const node of indexed.nodesById.values()) {
    if (!isCreateRowVariantNode(node)) {
      continue;
    }

    if (!hasConnectedConditionPort(indexed, node.id)) {
      continue;
    }

    const rawName = readPortDefaultValue(node, [
      "name",
      "variantName",
      "variant",
    ]);
    const variantName = String(rawName ?? "").trim();

    if (!variantName) {
      continue;
    }

    const rawOrder = readPortDefaultValue(node, ["order"]);
    const order =
      typeof rawOrder === "number" && Number.isFinite(rawOrder)
        ? rawOrder
        : rules.length;

    const condition = valueCompiler.compileInputValue(node.id, "condition");

    rules.push({
      variantName,
      order,
      condition: condition as CompiledValue<boolean>,
      dependencies: condition.dependencies,
    });
  }

  return rules.sort((left, right) => left.order - right.order);
}
